/*
 * Cart "Spend X more and get free shipping!" message.
 *
 * SCOPE: this script now only handles UNCOVERED FX markets — currencies with no
 * figure in the free_shipping_thresholds metafield. Covered markets (any EUR
 * market, plus every currency/country present in the metafield) are rendered
 * final, server-side, by snippets/free-shipping-bar.liquid; init() early-returns
 * for them via isCoveredMarket(). The notes below describe the uncovered path.
 *
 * SOURCE OF TRUTH = THE PER-MARKET THRESHOLD METAFIELD, THEN THE BANNER.
 *   window.MIMOA_SHIPPING.thresholds holds an explicit, pre-converted free
 *   shipping threshold per currency (byCurrency) plus the two EUR figures
 *   (in-EU vs rest-of-world). We use that FIRST, because it is keyed by the
 *   currency the shopper is actually paying in: it is available immediately on
 *   load and can never show the previous market's number or the wrong currency
 *   after a fast market switch. Its figures match the announcement banner for
 *   every market that has one.
 *
 *   Only currencies we have NO explicit threshold for (FX markets such as THB,
 *   where the real number depends on the live exchange rate) fall back to the
 *   amount the geolocation app prints in the announcement banner — e.g. Thailand
 *   shows "...สูงกว่า ฿7575" (7575 THB = 200 EUR at the real market rate).
 *   sync-shipping-banner.js mirrors that text into the carousel cell and stamps
 *   it on [data-shipping-banner-synced-value]; we read the amount straight out
 *   of it so the cart bar and the banner show the same number.
 *
 *   Why not recompute EUR -> local ourselves: the exchange rate derived from the
 *   cart is the discounted product ratio, not the real FX, so it lands on the
 *   wrong number and unlocks too early. byCurrency holds explicit pre-converted
 *   figures and the banner holds the app's converted figure, so we never divide.
 *
 * COMPARISON:
 *   Threshold (major units) comes from the metafield or banner. The live cart
 *   total (presentment minor units) comes from /cart.js. We scale them to the
 *   same units using the currency's number of decimals, which for Shopify's
 *   /cart.js is always x100 (see currencyScale).
 *
 *   The bar is cosmetic; real free shipping is enforced at checkout, so nothing
 *   here can be "cheated".
 *
 * Reveal / no-flash gating (PER ELEMENT):
 *   The theme re-renders the cart section on every quantity change, which
 *   paints its OWN hardcoded free-shipping message (the merchant's default
 *   threshold) for a frame before we recompute the real per-market number. To
 *   stop that flash we DO NOT use a one-shot <html> ready flag (that only gates
 *   the very first paint). Instead each <free-shipping-bar> is gated in CSS by:
 *
 *       free-shipping-bar:not([data-fs-applied]) { visibility: hidden; }
 *
 *   A bar is hidden until WE stamp data-fs-applied on it. A freshly INSERTED bar
 *   (the theme replaced the cart section) has no such attribute, so it stays
 *   hidden (slot reserved, no layout jump) until we touch it. Crucially we only
 *   stamp it once we have a CORRECT per-market value — if we cannot compute one
 *   yet we leave it hidden rather than reveal the theme's wrong default.
 *
 *   A quantity click does not cause ONE re-render — these themes paint an
 *   instant optimistic render and then a confirmed one once the server
 *   responds. So on each re-render we FREEZE every bar back to exactly what it
 *   last showed (text + aria) — done synchronously inside the MutationObserver
 *   microtask, which runs BEFORE paint, so the theme default never shows and an
 *   in-place bar never blanks. Only AFTER the render storm settles (a short
 *   debounce) do we read the live /cart.js total and apply the final message
 *   once. A 3s failsafe reveals everything so a bar is never stuck hidden if
 *   our JS dies.
 *
 * Progress fill:
 *   The fill is driven by the theme's native <progress-bar>, which reads the
 *   aria-valuenow / aria-valuemax we set (see setProgress) and animates to them
 *   (keyed by the bar's stable id, so it animates smoothly across re-renders).
 *   We no longer set a custom --fs-progress property.
 *
 * ELIGIBLE STATE:
 *   When the cart reaches the threshold we show a success message rather than
 *   hiding the bar. If the theme rendered its own (localised) success copy we
 *   keep it; otherwise we write window.MIMOA_SHIPPING.eligibleText (English
 *   fallback).
 */
(function () {
  var BAR_SELECTOR = 'free-shipping-bar';
  var BOLD_SELECTOR = '.bold';
  var BANNER_ATTR = 'data-shipping-banner-synced-value';
  var READY_ATTR = 'data-free-shipping-ready';
  var APPLIED_ATTR = 'data-fs-applied';
  var FAILSAFE_REVEAL_MS = 3000;
  // After a cart re-render, wait this long for the render storm (the theme
  // fires an instant optimistic render and then a confirmed one per click) to
  // settle before reading /cart.js and applying the final value. Keeps the
  // progress bar to a single smooth transition instead of one per render.
  var RECONCILE_DELAY_MS = 50;
  var DEFAULT_ELIGIBLE_TEXT = "You've unlocked free shipping!";

  var cart = null; // { currency, total } presentment, from /cart.js
  var fetchingCart = false;
  var pendingThen = null; // callback to run after an in-flight fetch finishes
  var isUpdating = false;
  var observer = null;
  var reconcileTimer = null;
  // The exact display we last wrote, so we can (a) tell our own observer echo
  // from a real re-render and (b) freeze the bar to it during a render storm.
  var lastApplied = null; // { now, max, spanHTML }

  function markReady() {
    if (!document.documentElement.hasAttribute(READY_ATTR)) {
      document.documentElement.setAttribute(READY_ATTR, '');
    }
  }

  function eachBar(fn) {
    Array.prototype.forEach.call(document.querySelectorAll(BAR_SELECTOR), fn);
  }

  // Reveal a bar (let CSS show it). Stamped once a bar carries the correct
  // message, and by the failsafe. data-fs-applied is NOT in the observer's
  // attributeFilter, so setting it never re-triggers the observer.
  function markApplied(bar) {
    bar.setAttribute(APPLIED_ATTR, '');
  }

  function revealAll() { eachBar(markApplied); }

  // The synced shipping banner text, e.g. "...above ฿7575" / "...above 1620 kr".
  function bannerText() {
    var el = document.querySelector('[' + BANNER_ATTR + ']');
    var t = el && el.getAttribute(BANNER_ATTR);
    if (t) return t;
    try {
      if (window.ShippingBannerCache && window.ShippingBannerCache.get) {
        return window.ShippingBannerCache.get() || '';
      }
    } catch (e) {}
    return '';
  }

  // Pull the threshold out of the banner text and return it in MAJOR units
  // (e.g. 7575, 1620, 130, 200, 879240), or null if none found. Handles symbol
  // or text currencies and , . space / nbsp / thin-space separators. Treats a
  // trailing group of 1-2 digits after a separator as decimals; everything else
  // as thousands grouping (our thresholds are whole numbers).
  function parseThreshold(text) {
    if (!text) return null;
    var tokens = text.match(/\d[\d.,\s]*\d|\d/g);
    if (!tokens || !tokens.length) return null;
    var tok = tokens[tokens.length - 1].replace(/\s/g, '');

    var lastSep = Math.max(tok.lastIndexOf('.'), tok.lastIndexOf(','));
    var val;
    if (lastSep === -1) {
      val = parseFloat(tok);
    } else {
      var dec = tok.slice(lastSep + 1);
      if (dec.length === 1 || dec.length === 2) {
        val = parseFloat(tok.slice(0, lastSep).replace(/[.,]/g, '') + '.' + dec);
      } else {
        val = parseFloat(tok.replace(/[.,]/g, ''));
      }
    }
    return isNaN(val) ? null : val;
  }

  // Explicit per-market threshold (MAJOR units) for the CURRENT cart currency,
  // taken from the metafield window.MIMOA_SHIPPING.thresholds — or null when we
  // have no explicit figure for this currency (e.g. FX markets like THB, where
  // only the announcement banner knows the converted number).
  //
  // Keyed by the currency the shopper is paying in, so it is available at once
  // on load and can never be stale or in the wrong currency after a market
  // switch. No FX is computed here: byCurrency holds explicit pre-converted
  // thresholds, and EUR uses the two fixed figures the merchant configured
  // (in-EU vs rest-of-world). EUR is handled BEFORE the generic byCurrency
  // lookup because byCurrency.EUR is the in-EU figure only.
  function localThreshold(cfg, currency, country) {
    var th = cfg.thresholds || {};
    var by = th.byCurrency || {};
    if (currency === 'EUR') {
      var eu = th.euCountries || [];
      if (country && eu.indexOf(country) !== -1) {
        return by.EUR != null ? by.EUR : null;
      }
      return th.eurRowThreshold != null ? th.eurRowThreshold
           : (by.EUR != null ? by.EUR : null);
    }
    if (by[currency] != null) return by[currency];
    return null;
  }

  // The shop -> presentment conversion rate Shopify is using for this market,
  // e.g. 7.65686 for DKK, 3.21145 for XCD. Exposed as window.Shopify.currency.rate.
  // EUR markets are the base, so their rate is 1. Returns null if unavailable, so
  // callers can fall back rather than compute a wrong number.
  function marketRate() {
    try {
      var cur = window.Shopify && window.Shopify.currency;
      if (cur) {
        if (cur.active === 'EUR') return 1;
        if (cur.rate) { var r = parseFloat(cur.rate); if (r > 0) return r; }
      }
    } catch (e) {}
    return null;
  }

  // Universal fallback threshold (MAJOR units, presentment) for ANY market that
  // has neither an explicit metafield figure nor an announcement banner — e.g.
  // small markets like Anguilla (XCD) the shipping app does not print a banner
  // for. We take the EUR free-shipping threshold (in-EU 150 vs rest-of-world
  // 200) and convert it with Shopify's own market rate, so every country shows a
  // sensible local figure instead of the theme's hardcoded default. Returns null
  // only if we cannot read a rate (then the bar stays gated rather than wrong).
  function rateThreshold(cfg, country) {
    var th = cfg.thresholds || {};
    var by = th.byCurrency || {};
    var eu = th.euCountries || [];
    var eurMajor = (country && eu.indexOf(country) !== -1)
      ? (by.EUR != null ? by.EUR : 150)
      : (th.eurRowThreshold != null ? th.eurRowThreshold : 200);
    var rate = marketRate();
    if (!rate) return null;
    return eurMajor * rate;
  }

  // Shopify's storefront /cart.js reports money in x100 "cents" for EVERY
  // presentment currency — including the zero-decimal ones. Verified live: a
  // ¥20,700 cart returns total_price 2070000 (JPY); a ₲790,000 cart returns
  // 79000000 (PYG). So to line the cart total up with the major-unit threshold
  // we ALWAYS scale the threshold x100.
  function currencyScale(currency) {
    return 100;
  }

  function formatMoney(mainUnits, currency, locale) {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(mainUnits);
    } catch (e) {
      return currency + ' ' + mainUnits.toFixed(2);
    }
  }

  // Build the comparison facts, or null to leave the bar gated (hidden).
  function resolve() {
    var cfg = window.MIMOA_SHIPPING || {};
    var locale = cfg.locale || 'en';
    var currency = (cart && cart.currency) || cfg.currency;
    if (!cart || typeof cart.total !== 'number' || !currency) return null;

    // Prefer the explicit per-currency threshold from the metafield. It is keyed
    // by the live cart currency, so it is always consistent with what the
    // shopper pays in and is available immediately on load — no waiting for the
    // banner, and never the previous market's number or the wrong currency. Only
    // currencies we have NO explicit threshold for (FX markets like THB) fall
    // back to the banner's converted amount.
    var thMajor = localThreshold(cfg, currency, cfg.country);
    if (thMajor == null) thMajor = parseThreshold(bannerText());
    // Last resort: no explicit figure and no banner for this market. Convert the
    // EUR threshold with Shopify's market rate so EVERY country shows a sane
    // local value instead of the theme's hardcoded default.
    if (thMajor == null) thMajor = rateThreshold(cfg, cfg.country);
    if (thMajor == null) return null;

    var scale = currencyScale(currency);
    return {
      currency: currency,
      locale: locale,
      scale: scale,
      thresholdMinor: Math.round(thMajor * scale),
      totalMinor: cart.total,
    };
  }

  function setProgress(progress, nowMinor, maxMinor) {
    if (!progress) return;
    progress.setAttribute('aria-valuemax', String(maxMinor));
    progress.setAttribute('aria-valuenow', String(Math.max(0, Math.min(nowMinor, maxMinor))));
  }

  // Snapshot exactly what a bar now shows. Used to (a) tell our own observer
  // echo from a real re-render and (b) freeze the bar to this during a storm.
  function recordLastApplied(bar) {
    var p = bar.querySelector('[role="progressbar"]');
    var span = bar.querySelector('span');
    lastApplied = {
      now: p ? p.getAttribute('aria-valuenow') : null,
      max: p ? p.getAttribute('aria-valuemax') : null,
      spanHTML: span ? span.innerHTML : null,
    };
  }

  // True when every bar's progress already shows the values we last wrote — i.e.
  // a MutationObserver callback was triggered by our OWN update being redelivered
  // rather than by a real theme re-render. Used to avoid a refetch loop.
  function barsMatchLastApplied() {
    if (!lastApplied) return false;
    var bars = document.querySelectorAll(BAR_SELECTOR);
    if (!bars.length) return false;
    for (var i = 0; i < bars.length; i++) {
      var p = bars[i].querySelector('[role="progressbar"]');
      if (!p) return false;
      if (p.getAttribute('aria-valuenow') !== lastApplied.now ||
          p.getAttribute('aria-valuemax') !== lastApplied.max) {
        return false;
      }
    }
    return true;
  }

  function fillBelowState(bar, remainingText) {
    bar.style.display = '';

    var bold = bar.querySelector(BOLD_SELECTOR);
    if (bold) {
      // Theme rendered its localised "Spend <bold>X</bold> more ..." copy.
      bold.textContent = remainingText;
      return;
    }

    // Theme rendered the "you are eligible" success state but we are below the
    // threshold. Rewrite the message. English fallback only; raising the theme's
    // own threshold so it always renders its localised below-state avoids this.
    var span = bar.querySelector('span');
    if (!span) return;
    span.textContent = '';
    span.appendChild(document.createTextNode('Spend '));
    var b = document.createElement('span');
    b.className = 'bold';
    b.textContent = remainingText;
    span.appendChild(b);
    span.appendChild(document.createTextNode(' more and get free shipping.'));
  }

  function fillEligibleState(bar) {
    bar.style.display = '';

    // If the theme rendered its own success state (no .bold "spend X more"
    // markup), it is already showing the localised eligible message — keep it.
    var bold = bar.querySelector(BOLD_SELECTOR);
    if (!bold) return;

    // Theme rendered the below-state ("Spend X more"); we have crossed the
    // threshold, so replace it with the eligible copy.
    var span = bar.querySelector('span');
    if (!span) return;
    var cfg = window.MIMOA_SHIPPING || {};
    span.textContent = cfg.eligibleText || DEFAULT_ELIGIBLE_TEXT;
  }

  function updateBar(bar, r) {
    var progress = bar.querySelector('[role="progressbar"]');

    if (r.totalMinor >= r.thresholdMinor) {
      setProgress(progress, r.thresholdMinor, r.thresholdMinor);
      fillEligibleState(bar);
    } else {
      setProgress(progress, r.totalMinor, r.thresholdMinor);
      var remainingMajor = (r.thresholdMinor - r.totalMinor) / r.scale;
      fillBelowState(bar, formatMoney(remainingMajor, r.currency, r.locale));
    }

    // Reveal this bar now that it carries the correct message, and remember the
    // exact display so we can freeze to it during the next re-render.
    markApplied(bar);
    recordLastApplied(bar);
  }

  // Restore every bar to the exact values/markup we last applied, WITHOUT
  // recomputing from a (possibly stale) cart. Called synchronously inside the
  // observer microtask on a re-render: it runs before the browser paints, so
  // the theme's freshly rendered default never shows, and because it writes the
  // same values that were already on screen the progress bar does not move. The
  // real new value is applied once, later, by the debounced reconcile — a single
  // smooth transition with no bounce. Returns false if we have nothing to
  // freeze to yet (very first interaction before any apply).
  function freezeToLastApplied() {
    if (!lastApplied) return false;
    isUpdating = true;
    if (observer) observer.disconnect();
    try {
      eachBar(function (bar) {
        var p = bar.querySelector('[role="progressbar"]');
        if (p && lastApplied.now != null) {
          p.setAttribute('aria-valuemax', lastApplied.max);
          p.setAttribute('aria-valuenow', lastApplied.now);
        }
        var span = bar.querySelector('span');
        if (span && lastApplied.spanHTML != null) span.innerHTML = lastApplied.spanHTML;
        markApplied(bar);
      });
    } finally {
      if (observer) connectObserver();
      isUpdating = false;
    }
    return true;
  }

  function connectObserver() {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-valuenow', BANNER_ATTR],
    });
  }

  function applyAll() {
    if (isUpdating) return;

    var r = resolve();
    if (!r) {
      // Can't compute a correct per-market threshold yet (e.g. an FX market
      // whose banner hasn't synced, or the cart isn't loaded). Do NOT reveal the
      // theme's own default bar here — its hardcoded number is wrong for the
      // market and showing it is exactly the bug we are fixing. Keep the bar
      // gated (hidden) and wait: the banner sync / cart fetch already in flight
      // will re-run us, and the 3s failsafe is the only last-resort reveal if
      // nothing ever resolves.
      markReady();
      return;
    }

    isUpdating = true;
    if (observer) observer.disconnect();
    try {
      eachBar(function (bar) { updateBar(bar, r); });
    } finally {
      if (observer) connectObserver();
      isUpdating = false;
    }
    markReady();
  }

  function refreshCart(then) {
    // Coalesce: if a fetch is already running, remember we want another pass
    // when it finishes (the cart may have changed again mid-flight) instead of
    // dropping the request.
    if (fetchingCart) { pendingThen = then || pendingThen; return; }
    fetchingCart = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (c && typeof c.total_price === 'number' && c.currency) {
          // If the market/currency changed under us (a fast market switch), drop
          // any frozen snapshot and re-gate every bar, so we never display the
          // previous market's amount or that amount under the new currency. The
          // next applyAll() reveals them again with the fresh, consistent value.
          if (cart && cart.currency !== c.currency) {
            lastApplied = null;
            eachBar(function (bar) { bar.removeAttribute(APPLIED_ATTR); });
          }
          cart = { currency: c.currency, total: c.total_price };
        }
      })
      .catch(function () { /* keep last known cart */ })
      .then(function () {
        fetchingCart = false;
        if (then) then();
        if (pendingThen) {
          var p = pendingThen;
          pendingThen = null;
          refreshCart(p);
        }
      });
  }

  // Debounced single reconcile: wait out the theme's render storm, then read
  // the live cart once and apply the final value in one transition.
  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      refreshCart(applyAll);
    }, RECONCILE_DELAY_MS);
  }

  // True when the current market has an explicit server-side threshold and the
  // Liquid snippet (snippets/free-shipping-bar.liquid) therefore already
  // rendered the final bar (with data-fs-applied) on every render. Mirrors the
  // snippet's precedence: per-country override, any EUR market, or an explicit
  // per-currency figure. This script only fills the UNCOVERED FX long tail.
  function isCoveredMarket() {
    var cfg = window.MIMOA_SHIPPING || {};
    var th = cfg.thresholds || {};
    var by = th.byCurrency || {};
    var byCountry = th.byCountry || {};
    if (cfg.country && byCountry[cfg.country] != null) return true;
    if (cfg.currency === 'EUR') return true; // in-EU figure or eurRowThreshold
    return by[cfg.currency] != null;
  }

  function init() {
    // Covered markets are fully handled server-side by the Liquid snippet, so
    // the server-rendered bar is already correct and visible. Do nothing for
    // them; this script only fills uncovered FX markets from the banner/rate.
    if (isCoveredMarket()) return;

    observer = new MutationObserver(function (mutations) {
      // Ignore mutations we caused ourselves while a write is in flight.
      if (isUpdating) return;

      var bumped = false;
      for (var i = 0; i < mutations.length && !bumped; i++) {
        var m = mutations[i];
        if (m.type === 'attributes' &&
            (m.attributeName === 'aria-valuenow' || m.attributeName === BANNER_ATTR)) {
          bumped = true;
          break;
        }
        if (m.type === 'childList') {
          for (var j = 0; j < m.addedNodes.length; j++) {
            var node = m.addedNodes[j];
            if (node.nodeType !== 1) continue;
            if (
              (node.matches && (node.matches(BAR_SELECTOR) || node.matches('[' + BANNER_ATTR + ']'))) ||
              (node.querySelector && (node.querySelector(BAR_SELECTOR) || node.querySelector('[' + BANNER_ATTR + ']')))
            ) {
              bumped = true;
              break;
            }
          }
        }
      }
      if (!bumped) return;

      // disconnect() does not reliably stop our own writes from being
      // redelivered here when observing childList + subtree, so drop any
      // callback where the bars already show exactly what we last wrote — that
      // is our echo (or our own freeze), not a real re-render.
      if (barsMatchLastApplied()) return;

      // A genuine theme re-render. We are inside a MutationObserver microtask,
      // which runs BEFORE the next paint, so freeze every bar back to exactly
      // what it last showed: the theme's default never paints, an in-place bar
      // does not blank out, and a freshly inserted bar is revealed with the
      // previous (correct) value. We deliberately do NOT recompute here — the
      // theme fires several renders per click, and re-applying a stale cart on
      // each one is what made the bar bounce. The real new value is applied
      // once, after the storm settles, as a single smooth transition.
      freezeToLastApplied();
      scheduleReconcile();
    });

    applyAll();
    refreshCart(applyAll);
    connectObserver();

    // Never leave a bar stuck hidden if something above failed.
    setTimeout(function () {
      markReady();
      revealAll();
    }, FAILSAFE_REVEAL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
